import { useQuery, useReadQuery } from '@apollo/client';
import { useIsTablet } from '@synoptic/ui-kit/responsive/hooks.js';
import { StickyTabNav } from '@synoptic/ui-kit/tab-nav/sticky-tab-nav.js';
import { clsx } from 'clsx';
import { Suspense, useEffect, useState } from 'react';
import {
  ClientLoaderFunction,
  Outlet,
  ShouldRevalidateFunction,
  useParams,
} from 'react-router';
import { shouldReportGraphQLError } from '@/apollo/graphql-error-reporting.ts';
import { makeSsrApolloClient } from '@/apollo/ssr-apollo-client.ts';
import { ErrorPage } from '@/components/error-page/error-page.tsx';
import { InteractionRequiredModal } from '@/components/interaction-required-modal/interaction-required-modal.tsx';
import { Page, PageContent } from '@/components/layout/page.tsx';
import { PageHeader } from '@/components/page-header/page-header.tsx';
import { NudgeFallback } from '@/components/streams/nudge-card/nudge-fallback.tsx';
import { StreamDeckNudge } from '@/components/streams/nudge-card/stream-deck-nudge/stream-deck-nudge.tsx';
import { getStreamNotifSoundStatus } from '@/components/streams/toggle-notif-sound/stream-notif-sound-status.ts';
import { adminTab } from '@/components/tab-link.css.ts';
import { TabLink } from '@/components/tab-link.tsx';
import { SoundStatus } from '@/components/toggle-sound/sound-status.ts';
import { flags } from '@/feature-flags/flags.ts';
import { useRedesign } from '@/feature-flags/redesign/use-redesign.ts';
import { useFeatureFlags } from '@/feature-flags/use-feature-flags.ts';
import { tokenProviderContext } from '@/request-context.ts';
import { getPageMeta } from '@/routes-utils/get-page-meta.ts';
import { NavLink } from '@/routes-utils/navigation.tsx';
import { paths } from '@/routes-utils/paths.ts';
import { checkUserInteractionWithAudio } from '@/utils/audio-utils/check-user-interaction-with-audio.ts';
import { StreamHasTimeSeriesQuery } from '../_app.p.streams_.$id.time-series/queries.ts';
import { tournamentNav } from '../_app.p.tournaments_.$id/page.css.ts';
import { useAuthInfo } from '../_app/use-auth-info.ts';
import { Route } from './+types/route.ts';
import { getStreamPageMeta } from './get-stream-page-meta.ts';
import {
  AuthenticatedStreamLoaderData,
  streamLoader,
  useStreamLoaderData,
} from './loader.ts';
import { StreamBannerFallback } from './stream-banner/stream-banner-fallback.tsx';
import { StreamBanner } from './stream-banner/stream-banner.tsx';
import { UnauthorizedStreamBanner } from './stream-banner/unauthorized-stream-banner.tsx';
import {
  streamPageAdminTabV2,
  streamPageSectionV2,
  streamPageTabV2,
  streamPageTabsV2,
} from './stream-page-v2.css.ts';
import { StreamPageV2 } from './stream-page-v2.tsx';
import { UnauthenticatedStreamQuery } from './unauthenticated-stream-query.ts';

type StreamTab = {
  to: string;
  label: string;
  end?: boolean;
  admin?: boolean;
};

const StreamPageComponent = () => {
  const streamId = useParams().id as string;
  const { streamQueryRef, authenticated } = useStreamLoaderData();
  const { data } = useReadQuery(streamQueryRef);
  const stream = data?.stream;
  const isTablet = useIsTablet();

  const { isEnterprise, isAdmin, id: meId } = useAuthInfo();
  const redesign = useRedesign();

  const [interactionModalOpen, setInteractionModalOpen] = useState(false);

  const isAuthor = meId === stream?.author.id;
  const chartsEnabled = useFeatureFlags(flags.streamTimeSeries);
  const { data: timeSeriesData } = useQuery(StreamHasTimeSeriesQuery, {
    variables: { id: streamId },
    fetchPolicy: 'cache-and-network',
    skip: !authenticated || !chartsEnabled || isAuthor,
  });
  const showCharts =
    chartsEnabled && (isAuthor || Boolean(timeSeriesData?.streamHasTimeSeries));

  useEffect(() => {
    if (
      authenticated &&
      stream &&
      getStreamNotifSoundStatus(stream.id) === SoundStatus.on
    )
      checkUserInteractionWithAudio(() => setInteractionModalOpen(true));
  }, [authenticated, setInteractionModalOpen, stream]);

  if (!stream) {
    return null;
  }

  const apiPath =
    meId === stream.author.id
      ? paths.makeStreamApi(streamId)
      : paths.makeStreamReaderApi(streamId);

  const tabs: StreamTab[] = [
    {
      to: paths.makeStream(streamId),
      label: 'Data',
      end: true,
    },
    { to: paths.makeStreamDiscussions(streamId), label: 'Discussion' },
    { to: apiPath, label: 'API' },
    ...(showCharts
      ? [{ to: paths.makeStreamTimeSeries(streamId), label: 'Charts' }]
      : []),
    ...(isEnterprise
      ? [
          {
            to: paths.makeStreamRegionStats(streamId),
            label: 'Region stats',
          },
        ]
      : []),
    ...(isAdmin
      ? [
          { to: paths.makeStreamStats(streamId), label: 'Stats', admin: true },
          {
            to: paths.makeStreamRoutes(streamId),
            label: 'Routes',
            admin: true,
          },
          {
            to: paths.makeStreamExport(streamId),
            label: 'Enterprise Export',
            admin: true,
          },
        ]
      : []),
  ];

  return (
    <>
      {redesign ? (
        <div className={streamPageSectionV2}>
          <nav className={streamPageTabsV2} aria-label="Stream">
            {tabs.map((tab) => (
              <NavLink
                key={tab.to}
                end={tab.end}
                replace
                className={clsx(
                  streamPageTabV2,
                  tab.admin && streamPageAdminTabV2,
                )}
                to={tab.to}
                preventScrollReset
              >
                {tab.label}
              </NavLink>
            ))}
          </nav>
          <Outlet />
        </div>
      ) : (
        <>
          <StickyTabNav
            className={tournamentNav}
            size={isTablet ? 'large' : 'small'}
          >
            {tabs.map((tab) => (
              <TabLink
                key={tab.to}
                end={tab.end}
                to={tab.to}
                preventScrollReset
                className={tab.admin ? adminTab : undefined}
              >
                {tab.label}
              </TabLink>
            ))}
          </StickyTabNav>
          <Outlet />
        </>
      )}

      {authenticated && (
        <InteractionRequiredModal
          open={interactionModalOpen}
          onOpenChange={setInteractionModalOpen}
        />
      )}
    </>
  );
};

export const clientLoader = streamLoader;
(clientLoader as unknown as ClientLoaderFunction).hydrate = true;

const hasChangedSearchParam = (
  nextUrl: URL,
  currentUrl: URL,
  param: string,
) => {
  return nextUrl.searchParams.get(param) !== currentUrl.searchParams.get(param);
};

export const shouldRevalidate: ShouldRevalidateFunction = ({
  currentUrl,
  nextUrl,
  currentParams,
  nextParams,
  defaultShouldRevalidate,
}) => {
  if (
    currentParams.id === nextParams.id &&
    ['q', 'idempotencyKey', 'region', 'series'].some((param) =>
      hasChangedSearchParam(nextUrl, currentUrl, param),
    )
  ) {
    // skip revalidation when searching for posts, filtering region stats or switching charts
    return false;
  }

  return defaultShouldRevalidate;
};

export const loader = async ({ params, context }: Route.LoaderArgs) => {
  const tokens = await context.get(tokenProviderContext).getTokens();

  if (tokens) {
    return null;
  }

  const id = params.id as string;
  const client = makeSsrApolloClient();
  const { data, errors } = await client.query({
    query: UnauthenticatedStreamQuery,
    variables: { id },
    errorPolicy: 'all',
  });

  const unexpectedError = errors?.find(shouldReportGraphQLError);

  if (unexpectedError) {
    throw unexpectedError;
  }

  return { unauthenticatedData: data };
};

export const meta = ({ loaderData }: Route.MetaArgs) => {
  const stream =
    loaderData &&
    'unauthenticatedData' in loaderData &&
    loaderData.unauthenticatedData?.stream;
  return stream ? getPageMeta(getStreamPageMeta(stream)) : getPageMeta();
};
export const ErrorBoundary = ErrorPage;

const StreamPageV1 = () => {
  const { authenticated } = useStreamLoaderData();

  const isMobile = !useIsTablet();

  return (
    <Page>
      {authenticated && <PageHeader>Stream</PageHeader>}
      <PageContent>
        {authenticated && !isMobile && (
          <Suspense fallback={<NudgeFallback />}>
            <NudgeCard />
          </Suspense>
        )}
        <Suspense fallback={<StreamBannerFallback />}>
          {authenticated ? <StreamBanner /> : <UnauthorizedStreamBanner />}
        </Suspense>
        <Suspense fallback={null}>
          <StreamPageComponent />
        </Suspense>
      </PageContent>
    </Page>
  );
};

export default function Stream() {
  const redesign = useRedesign();

  if (redesign) {
    return (
      <StreamPageV2>
        <Suspense fallback={null}>
          <StreamPageComponent />
        </Suspense>
      </StreamPageV2>
    );
  }

  return <StreamPageV1 />;
}

const NudgeCard = () => {
  const { streamQueryRef } =
    useStreamLoaderData() as AuthenticatedStreamLoaderData;
  const { data } = useReadQuery(streamQueryRef);
  const stream = data?.stream;

  if (!stream) {
    return null;
  }

  return <StreamDeckNudge />;
};
